import { useState } from "react";
import { uploadCategory } from "@/lib/uploadCategory";
import { CloudDownload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSpace } from "@/context/SpaceContext";
import { useDrive } from "@/context/DriveContext";
import { translateError } from "@/i18n/errors";

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const s = document.createElement("script");
  Object.assign(s, { src, async: true, onload: resolve, onerror: reject });
  document.head.appendChild(s);
});

const pickFiles = (cfg, token) => new Promise((resolve) => {
  const p = window.google.picker;
  const view = new p.DocsView(p.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false);
  const builder = new p.PickerBuilder()
    .addView(view)
    .enableFeature(p.Feature.MULTISELECT_ENABLED)
    .setOAuthToken(token).setDeveloperKey(cfg.api_key).setOrigin(window.location.origin)
    .setCallback((d) => {
      if (d.action === p.Action.PICKED) resolve(d.docs);
      else if (d.action === p.Action.CANCEL) resolve([]);
    });
  if (cfg.app_id) builder.setAppId(cfg.app_id);
  builder.build().setVisible(true);
});

// "Importer depuis Google Drive": Google Picker in the browser, the server copies each picked APK into storage.
export const DriveImportButton = ({ appId = null, onDone }) => {
  const { t } = useI18n();
  const { base } = useSpace();
  const drive = useDrive();
  const [busy, setBusy] = useState(false);
  const cfg = drive?.cfg;

  const run = async () => {
    if (!cfg.connected) return drive.connect();
    setBusy(true);
    try {
      const [{ data }] = await Promise.all([api.get(`${base}/drive/token`), loadScript("https://apis.google.com/js/api.js")]);
      await new Promise((r) => window.gapi.load("picker", r));
      const docs = await pickFiles(cfg, data.access_token);
      for (const doc of docs) {
        try {
          const { data: res } = await api.post(`${base}/drive/import`, { file_id: doc.id, app_id: appId, category: appId ? undefined : uploadCategory(base) });
          toast.success((res.result === "new_app"
            ? t("bulk.newApp", { name: res.app.name })
            : t("bulk.newVersion", { name: res.app.name, v: res.version.label })) + (res.dedup ? ` · ${t("upload.dedup")}` : ""));
          onDone?.(res.version);
        } catch (err) {
          toast.error(`${doc.name} — ${translateError(err, t)}`);
        }
      }
    } catch (err) {
      toast.error(err?.response ? translateError(err, t) : t("drive.failed"));
      drive.refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;
  return (
    <div className="min-w-0 space-y-1" data-testid="drive-import">
      <button type="button" className="btn-red btn-sm max-w-full" disabled={!cfg.enabled || busy} onClick={run} data-testid="drive-import-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudDownload className="h-4 w-4" />} {t("drive.import")}
      </button>
      {!cfg.enabled && <p className="text-xs text-neutral-500" data-testid="drive-import-soon">{t("drive.soon")}</p>}
    </div>
  );
};
